import { computed, Injectable, signal } from '@angular/core';
import { BusinessCategory, Lead, Priority } from '../lead.model';

@Injectable({
  providedIn: 'root',
})
export class LeadFilterService {
  public priority = signal<Priority | undefined>(undefined);
  public daysToDueDate = signal<number | undefined>(undefined);
  public businessCategory = signal<BusinessCategory | undefined>(undefined);
  public leadType = signal<string[]>([]);
  public businessCategoryOptions = signal<BusinessCategory[]>([]);
  public leadTypeOptions = signal<string[]>([]);
  public cityOptions = signal<string[]>([]);
  public streetOptions = signal<string[]>([]);
  public districtOptions = signal<string[]>([]);
  public cityFilter = signal<string[]>([]);
  public postalCodePrefix = signal<string[]>([]);
  public streetFilter = signal<string[]>([]);
  public districtFilter = signal<string[]>([]);

  public filterPredicate = computed<(lead: Lead) => boolean>(() => {
    return (lead: Lead) => {
      return (
        (this.isPostalCodePrefixEmpty() ||
          this.postalCodePrefix()
            .map((prefix) => lead.address.postalCode.startsWith(prefix))
            .reduce((previous, current) => previous || current, false)) &&
        (this.noCitySelected() ||
          this.cityFilter().includes(lead.address.city)) &&
        (this.detailedFiltersActive()
          ? (this.noStreetSelected() ||
            this.streetFilter().includes(lead.address.street)) &&
          (this.noDistrictSelected() ||
            this.districtFilter().includes(lead.address.district))
          : true) &&
        (this.priority() == undefined || this.priority() === lead.priority) &&
        this.matchesDueDateFilter(lead) &&
        (this.noLeadTypeSelected() ||
          (lead.leadType != null && this.leadType().includes(lead.leadType))) &&
        (this.businessCategory() == undefined ||
          this.businessCategory() === lead.businessCategory)
      );
    };
  });

  public noCitySelected = computed<boolean>(() => {
    return this.cityFilter().length === 0 || !this.cityFilter()[0];
  });

  public isPostalCodePrefixEmpty = computed<boolean>(() => {
    return this.postalCodePrefix().length === 0 || !this.postalCodePrefix()[0];
  });

  public noStreetSelected = computed<boolean>(() => {
    return this.streetFilter().length === 0;
  });

  public detailedFiltersActive = computed<boolean>(() => {
    return !this.noCitySelected() || !this.isPostalCodePrefixEmpty();
  });

  public noDistrictSelected = computed<boolean>(() => {
    return this.districtFilter().length === 0;
  });

  public noLeadTypeSelected = computed<boolean>(() => {
    return this.leadType().length === 0;
  });

  private matchesDueDateFilter(lead: Lead): boolean {
    const maxDays = this.daysToDueDate();
    if (maxDays == undefined) {
      return true;
    }

    if (lead.dueDate == null) {
      return false;
    }

    const dueDate = this.parseDateOnly(lead.dueDate);
    if (!dueDate) {
      return false;
    }

    const today = this.startOfDay(new Date());
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const dayDifference = Math.floor(
      (dueDate.getTime() - today.getTime()) / millisecondsPerDay,
    );

    return dayDifference >= 0 && dayDifference <= maxDays;
  }

  private parseDateOnly(dateString: string): Date | undefined {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
    if (!match) {
      return undefined;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);

    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return undefined;
    }

    return parsed;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}
